// StepperContext.js - FIXED with nights_covered logic + API integration
import React, { useState, createContext, useContext, useEffect } from "react";
import httpService from "../../common/httpService";
import { useAuth } from "../../common/AuthContext";
import { useParams } from "react-router-dom";

const StepperContext = createContext();

export const StepperProvider = ({ children }) => {
  const { mode } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [invoice, setInvoice] = useState({});
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { myConferenceId } = useAuth();

  // New states
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [lateCheckOut, setLateCheckOut] = useState(false);
  const [earlyCheckIn, setEarlyCheckIn] = useState(false);
  const [totalNights, setTotalNights] = useState(1);
  const [roomType, setRoomType] = useState();

  // 🔥 NEW: nights_covered from API
  const [nightsCovered, setNightsCovered] = useState(0);
  const [speakerInfo, setSpeakerInfo] = useState(null);

  // room states
  const initialValue = {
    roomType: "",
    occupantName: "",
    specialRequests: "",
    checkInDate: "",
    checkOutDate: "",
    lateCheckOut: false,
    earlyCheckIn: false,
    totalNights: "",
  };

  const [rooms, setRooms] = useState([initialValue]);

  // invoice state
  const [mainRoom, setMainRoom] = useState(null);
  const [otherRooms, setOtherRooms] = useState([]);
  const [reservationDetails, setReservationDetails] = useState(null);
  const [reservationId, setReservationId] = useState(0);
  const [mainRoomId, setMainRoomId] = useState(0);

  const completeStep = (stepIndex) => {
    if (!completedSteps.includes(stepIndex)) {
      setCompletedSteps((prev) => [...prev, stepIndex]);
    }
    setCurrentStep(stepIndex + 1);
  };

  // 🔥 NEW: Fetch Speaker Info API with local token
  const fetchSpeakerInfo = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      console.log("🔥 Fetching speaker info...");
      const response = await fetch("https://eventscons.com/eventbackendnew/api/speakers/info", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      console.log("✅ Speaker Info:", data);

      // Extract nights_covered (adjust path based on actual API response)
      const nights = data?.nights_covered || data?.data?.nights_covered || 0;
      setNightsCovered(nights);
      setSpeakerInfo(data);

    } catch (error) {
      console.error("❌ Speaker API Error:", error);
    }
  };

  // 🔥 Calculate Chargeable Nights (Main Logic)
  const getChargeableNights = () => {
    // First room (main room) ke totalNights se compare karo
    const mainRoomNights = rooms[0]?.totalNights || totalNights || 0;
    
    // Agar total nights > nights_covered, to extra nights charge @ $225/night
    const chargeableNights = Math.max(0, mainRoomNights - nightsCovered);
    return chargeableNights;
  };

  // 🔥 Calculate Invoice Amount
  const calculateInvoiceAmount = () => {
    const chargeableNights = getChargeableNights();
    const roomOnlyAmount = chargeableNights * 225; // $225 per night
    
    return {
      chargeableNights,
      roomOnlyAmount,
      totalNights: rooms[0]?.totalNights || totalNights || 0,
      nightsCovered,
      extraNightsCharge: roomOnlyAmount
    };
  };

  const fetchReservations = async () => {
    if (!myConferenceId) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authorization token is missing.");

      const response = await httpService({
        method: "GET",
        url: `${BASE_URL}/reservations/rooms/${myConferenceId}`,
        headers: { Authorization: `Bearer ${token}` },
        withToast: false,
        showLoader: true,
      });

      const responseData = response;

      if (responseData?.status === "success" && responseData?.data?.length > 0) {
        const reservation = responseData.data?.[0];

        setReservationId(reservation?.id);
        setReservationDetails({
          id: reservation.id,
          userId: reservation.user_id,
          roomCount: reservation.room_count,
          companionsCount: reservation.companions_count,
          updateDeadline: reservation.update_deadline,
          conferenceId: reservation.conference_id,
        });

        const roomsData = reservation.rooms || [];
        const speakerRoom = reservation.rooms?.[0];

        setMainRoomId(speakerRoom?.id);

        setCheckInDate(speakerRoom?.check_in_date);
        setCheckOutDate(speakerRoom?.check_out_date);
        setLateCheckOut(speakerRoom?.late_check_out);
        setEarlyCheckIn(speakerRoom?.early_check_in);
        setTotalNights(speakerRoom?.total_nights);
        setRoomType({
          value: speakerRoom?.room_type,
          label: speakerRoom?.room_type,
        });

        // 🔥 Set other rooms (slice(1) for additional rooms)
        setRooms(
          roomsData.slice(1).map((room) => ({
            id: room?.id,
            roomType: {
              value: room?.room_type,
              label: room?.room_type,
            },
            occupantName: room.occupant_name || "",
            specialRequests: room.special_requests || "",
            checkInDate: room.check_in_date,
            checkOutDate: room.check_out_date,
            lateCheckOut: !!room.late_check_out,
            earlyCheckIn: !!room.early_check_in,
            totalNights: room.total_nights,
          }))
        );
      }
    } catch (error) {
      console.error("Fetch Reservations Error:", error);
    }
  };

  // ✅ Auto Calculate Total Nights When Dates Change (MAIN ROOM ONLY)
  useEffect(() => {
    if (checkInDate && checkOutDate && rooms[0]) {
      const start = new Date(checkInDate);
      const end = new Date(checkOutDate);
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      const calculatedNights = diff > 0 ? diff : 0;

      // 🔥 Update MAIN ROOM only
      setTotalNights(calculatedNights);
      setRooms(prev => prev.map((room, index) => 
        index === 0 ? { ...room, totalNights: calculatedNights } : room
      ));
    }
  }, [checkInDate, checkOutDate]);

  // 🔥 Load speaker info on mount
  useEffect(() => {
    fetchSpeakerInfo();
  }, []);

  // 🔥 Update invoice whenever chargeable nights change
  useEffect(() => {
    const invoiceData = calculateInvoiceAmount();
    console.log("💰 Invoice Calculation:", invoiceData);
    
    setInvoice({
      ...invoice,
      roomOnly: invoiceData.roomOnlyAmount,
      chargeableNights: invoiceData.chargeableNights,
      nightsCovered,
      totalNights: invoiceData.totalNights,
      extraNightsCharge: invoiceData.extraNightsCharge
    });
  }, [totalNights, nightsCovered, rooms]);

  useEffect(() => {
    if (mode === "edit") {
      fetchReservations();
    }
  }, [myConferenceId]);

  return (
    <StepperContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        completedSteps,
        completeStep,
        invoice,
        setInvoice,
        checkInDate,
        setCheckInDate,
        checkOutDate,
        setCheckOutDate,
        lateCheckOut,
        setLateCheckOut,
        earlyCheckIn,
        setEarlyCheckIn,
        totalNights,
        setTotalNights,
        roomType,
        setRoomType,
        rooms,
        setRooms,
        mainRoom,
        setMainRoom,
        otherRooms,
        setOtherRooms,
        reservationDetails,
        setReservationDetails,
        reservationId,
        mainRoomId,
        nightsCovered,
        getChargeableNights,
        calculateInvoiceAmount,
        speakerInfo,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};

export const useStepper = () => useContext(StepperContext);